// src/features/programacaodmais1/ProgramacaoDmais1Page.jsx
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import api from "../../api/axios";
import "./ProgramacaoDmais1Page.css";

/* ===== dayjs (TZ BR) ===== */
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("pt-br");

const TZ = "America/Sao_Paulo";
dayjs.tz.setDefault(TZ);

/* ===== Helpers ===== */
const toTodayYMD = () => dayjs().tz(TZ).format("YYYY-MM-DD");
const toTomorrowYMD = () => dayjs().tz(TZ).add(1, "day").format("YYYY-MM-DD");
const fromYMD = (ymd) =>
  dayjs.tz(`${ymd || toTodayYMD()} 12:00`, "YYYY-MM-DD HH:mm", TZ);
const DATE_WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"];
const PRINT_PAGE_MM = { width: 594, height: 420, margin: 4 };
const PRINT_SCALE_SAFETY = 0.9;

/** Parser robusto para 'YYYY-MM-DD' -> Date (UTC 12:00) */
const parseYMD = (ymd) => {
  if (!ymd || typeof ymd !== "string") return null;
  const [yStr, mStr, dStr] = ymd.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  const d = Number(dStr);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
};

const toBRDate = (ymd) => {
  const dt = parseYMD(ymd);
  if (!dt || isNaN(dt.getTime())) return ymd || "";
  return new Intl.DateTimeFormat("pt-BR", { timeZone: TZ }).format(dt);
};

const toHHMM = (s) => {
  if (s == null) return "";
  const raw = String(s).trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(raw)) return raw.slice(11, 16);

  const m = raw.match(/^(\d{1,3}):([0-5]\d)(?::([0-5]\d))?$/);
  if (!m) {
    const m2 = raw.match(/(\d{1,2}):(\d{2})/);
    if (m2) return `${String(m2[1]).padStart(2, "0")}:${m2[2]}`;
    return raw;
  }
  return `${String(Number(m[1])).padStart(2, "0")}:${m[2]}`;
};

const nz = (v, alt = "—") => (v === 0 ? "0" : v ? String(v) : alt);

function pick(obj, keys, fallback = undefined) {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
}

/** Gera chave estável para automação (sem acentos / caracteres estranhos) */
const slugify = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // remove acentos
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80);

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
}

/* ===== Header cell helper ===== */
const Th = ({ children, className = "", ...rest }) => {
  const text = typeof children === "string" ? children.trim() : "";
  const isMultiWord = /\s+/.test(text);
  return (
    <th className={className} {...rest}>
      {typeof children === "string" ? (
        <span className={`th-label${isMultiWord ? " th--mw" : ""}`}>{text}</span>
      ) : (
        children
      )}
    </th>
  );
};

/* ===== FitTable (sem scroll, encaixa na tela usando ZOOM) ===== */
function FitTable({ children, minZoom = 0.55 }) {
  const wrapRef = useRef(null);
  const tableRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const lastZoomRef = useRef(1);

  const recompute = useCallback(() => {
    const wrap = wrapRef.current;
    const table = tableRef.current;
    if (!wrap || !table) return;

    const cw = wrap.clientWidth || 0;
    const tw = table.scrollWidth || 0;

    if (!cw || !tw) {
      setZoom(1);
      lastZoomRef.current = 1;
      return;
    }

    const style = getComputedStyle(wrap);
    const padLeft = Number.parseFloat(style.paddingLeft) || 0;
    const padRight = Number.parseFloat(style.paddingRight) || 0;
    const safeCw = Math.max(0, cw - padLeft - padRight - 2);

    let z = Math.min(1, safeCw / tw);
    z = Math.max(minZoom, z);

    // evita "vibração" por ajustes mínimos
    const rounded = Math.floor(z * 1000) / 1000;
    if (Math.abs(rounded - lastZoomRef.current) < 0.005) return;

    lastZoomRef.current = rounded;
    setZoom(rounded);
  }, [minZoom]);

  useLayoutEffect(() => {
    recompute();

    const wrap = wrapRef.current;
    const table = tableRef.current;
    if (!wrap || !table) return;

    const ro = new ResizeObserver(() => recompute());
    ro.observe(wrap);
    ro.observe(table);

    const onResize = () => recompute();
    window.addEventListener("resize", onResize);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [recompute, children]);

  const onlyChild = React.Children.only(children);
  const childWithRef = React.cloneElement(onlyChild, { ref: tableRef });

  return (
    <div className="d1-tablewrap" ref={wrapRef}>
      <div className="d1-tableZoom" style={{ zoom }}>
        {childWithRef}
      </div>
    </div>
  );
}

/* ===== Página ===== */
function DateDropdown({
  value,
  onChange,
  ariaLabel = "Selecionar data",
}) {
  const rootRef = useRef(null);
  const selectedDate = useMemo(() => fromYMD(value), [value]);
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => selectedDate.startOf("month"));

  useEffect(() => {
    if (!open) setViewMonth(selectedDate.startOf("month"));
  }, [selectedDate, open]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const selectedYMD = selectedDate.format("YYYY-MM-DD");
  const todayYMD = toTodayYMD();
  const monthStart = viewMonth.startOf("month");
  const gridStart = monthStart.subtract(monthStart.day(), "day");
  const calendarDays = Array.from({ length: 42 }, (_, index) =>
    gridStart.add(index, "day")
  );

  const handlePick = (ymd) => {
    onChange(ymd);
    setOpen(false);
  };

  return (
    <div className={`d1-dateDropdown${open ? " is-open" : ""}`} ref={rootRef}>
      <button
        className="d1-dateButton"
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="d1-dateButtonValue">{toBRDate(selectedYMD)}</span>
      </button>

      {open && (
        <div className="d1-datePanel" role="dialog" aria-label="Calendário">
          <div className="d1-datePanelHead">
            <button
              className="d1-dateNav"
              type="button"
              onClick={() => setViewMonth((prev) => prev.subtract(1, "month"))}
              aria-label="Mês anterior"
            >
              {"<"}
            </button>
            <div className="d1-datePanelTitle">
              {viewMonth.format("MMMM [de] YYYY")}
            </div>
            <button
              className="d1-dateNav"
              type="button"
              onClick={() => setViewMonth((prev) => prev.add(1, "month"))}
              aria-label="Próximo mês"
            >
              {">"}
            </button>
          </div>

          <div className="d1-dateWeekdays">
            {DATE_WEEKDAYS.map((weekday, index) => (
              <span key={`${weekday}-${index}`} className="d1-dateWeekday">
                {weekday}
              </span>
            ))}
          </div>

          <div className="d1-dateGrid">
            {calendarDays.map((day) => {
              const ymd = day.format("YYYY-MM-DD");
              const isOutside = day.month() !== viewMonth.month();
              const isSelected = ymd === selectedYMD;
              const isToday = ymd === todayYMD;

              return (
                <button
                  key={ymd}
                  className={[
                    "d1-dateDay",
                    isOutside ? "is-outside" : "",
                    isSelected ? "is-selected" : "",
                    isToday ? "is-today" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  type="button"
                  onClick={() => handlePick(ymd)}
                  aria-pressed={isSelected}
                >
                  {day.date()}
                </button>
              );
            })}
          </div>

          <div className="d1-datePanelFoot">
            <button
              className="d1-dateAction"
              type="button"
              onClick={() => handlePick(todayYMD)}
            >
              Hoje
            </button>
            <button
              className="d1-dateAction d1-dateActionGhost"
              type="button"
              onClick={() => setOpen(false)}
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProgramacaoDmais1Page({ onBack }) {
  const shellRef = useRef(null);
  const [dataFiltro, setDataFiltro] = useState(toTodayYMD());
  const [autoResolved, setAutoResolved] = useState(false);
  const [userPicked, setUserPicked] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [rows, setRows] = useState([]);
  const [printScale, setPrintScale] = useState(1);

  const onChangeData = useCallback((nextValue) => {
    const v =
      typeof nextValue === "string" ? nextValue : nextValue?.target?.value;
    setUserPicked(true);
    setAutoResolved(true);
    setDataFiltro(v || toTodayYMD());
  }, []);

  useEffect(() => {
    let alive = true;

    async function decidirDataInicial() {
      if (userPicked) {
        if (alive) setAutoResolved(true);
        return;
      }

      const hoje = toTodayYMD();
      const amanha = toTomorrowYMD();

      try {
        const r = await api.get("/programacao-dmais1/aceitas", {
          params: { data: amanha },
        });

        const list = r.data?.rows || r.data?.data || [];
        if (!alive) return;

        if (Array.isArray(list) && list.length > 0) {
          setDataFiltro(amanha);
        } else {
          setDataFiltro(hoje);
        }
      } catch {
        if (alive) setDataFiltro(hoje);
      } finally {
        if (alive) setAutoResolved(true);
      }
    }

    decidirDataInicial();
    return () => {
      alive = false;
    };
  }, [userPicked]);

  useEffect(() => {
    if (!autoResolved) return;
    let alive = true;

    async function carregar() {
      setCarregando(true);
      setErro("");
      setOkMsg("");

      try {
        const r = await api.get("/programacao-dmais1/aceitas", {
          params: { data: dataFiltro },
        });

        const list = r.data?.rows || r.data?.data || [];
        if (alive) setRows(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!alive) return;
        setErro(
          e?.response?.data?.error ||
            e?.message ||
            "Erro ao carregar Programação D+1"
        );
        setRows([]);
      } finally {
        if (alive) setCarregando(false);
      }
    }

    carregar();
    return () => {
      alive = false;
    };
  }, [dataFiltro, autoResolved]);

  /* ===== Agrupa por Gerência ===== */
  const grupos = useMemo(() => {
    const m = new Map();

    for (const r of rows) {
      const ger = String(
        pick(r, ["gerencia", "UP", "up", "gerencia_nome"], "—")
      ).trim();

      if (!m.has(ger)) m.set(ger, []);
      m.get(ger).push(r);
    }

    const asOrd = (v) => String(v ?? "").padStart(2, "0");

    return Array.from(m.entries())
      .map(([gerencia, itens]) => {
        itens.sort((a, b) => {
          const pa = pick(a, ["prioridade", "po", "prio"], "");
          const pb = pick(b, ["prioridade", "po", "prio"], "");
          return asOrd(pa).localeCompare(asOrd(pb));
        });
        return { gerencia, itens };
      })
      .sort((a, b) => a.gerencia.localeCompare(b.gerencia));
  }, [rows]);

  const COLS = 16;

  const totalLinhas = rows.length;
  const totalGerencias = grupos.length;
  const geradoEm = dayjs().tz(TZ).format("DD/MM/YYYY HH:mm");

  const computePrintScale = useCallback(() => {
    const shell = shellRef.current;
    if (!shell) return 1;

    const mmToPx = (mm) => (mm * 96) / 25.4;
    const printableWidthPx = mmToPx(
      PRINT_PAGE_MM.width - PRINT_PAGE_MM.margin * 2
    );
    const printableHeightPx = mmToPx(
      PRINT_PAGE_MM.height - PRINT_PAGE_MM.margin * 2
    );

    const width = Math.max(
      shell.scrollWidth || 0,
      shell.offsetWidth || 0,
      Math.ceil(shell.getBoundingClientRect().width || 0)
    );
    const height = Math.max(
      shell.scrollHeight || 0,
      shell.offsetHeight || 0,
      Math.ceil(shell.getBoundingClientRect().height || 0)
    );

    if (!width || !height) {
      setPrintScale(1);
      return 1;
    }

    const scale = Math.min(
      1,
      (printableWidthPx / width) * PRINT_SCALE_SAFETY,
      (printableHeightPx / height) * PRINT_SCALE_SAFETY
    );
    const next = Math.max(0.2, Math.round(scale * 1000) / 1000);
    setPrintScale((prev) => (Math.abs(prev - next) < 0.005 ? prev : next));
    return next;
  }, []);

  useLayoutEffect(() => {
    let raf1 = 0;
    let raf2 = 0;
    const shell = shellRef.current;
    if (!shell) return undefined;

    const schedule = () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
          computePrintScale();
        });
      });
    };

    schedule();

    const ro = new ResizeObserver(() => schedule());
    ro.observe(shell);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      ro.disconnect();
    };
  }, [computePrintScale, rows, carregando, erro, okMsg]);

  useEffect(() => {
    const handleBeforePrint = () => {
      computePrintScale();
    };

    window.addEventListener("beforeprint", handleBeforePrint);
    return () => {
      window.removeEventListener("beforeprint", handleBeforePrint);
    };
  }, [computePrintScale]);

  const onPrint = useCallback(() => {
    computePrintScale();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
      });
    });
  }, [computePrintScale]);

  const buildEmailResumo = () => {
    const br = toBRDate(dataFiltro);
    const lines = [];

    lines.push(`PROGRAMAÇÃO D+1 — ${br}`);
    lines.push(`Gerado em: ${geradoEm}`);
    lines.push(
      `Total Gerências: ${totalGerencias} | Total Programações: ${totalLinhas}`
    );
    lines.push("");

    for (const g of grupos) {
      lines.push(`=== ${g.gerencia} (${g.itens.length} programações) ===`);

      for (const r of g.itens) {
        const tipo = nz(pick(r, ["tipo_programacao", "tipoProgramacao"], ""), "");
        const prio = nz(pick(r, ["prioridade", "po", "prio"], ""), "");
        const coord = nz(pick(r, ["coordenador"], ""), "");
        const ativo = nz(pick(r, ["ativo"], ""), "");
        const ativ = nz(
          pick(r, ["atividade", "atividade_prog", "atividade_exibicao"], ""),
          ""
        );
        const qtd = nz(
          pick(
            r,
            ["qtd", "quantidade", "quantidade_prog", "quantidade_exibicao"],
            ""
          ),
          ""
        );
        const part = nz(pick(r, ["sbOrigem", "local_partida", "sb_origem"], ""), "");
        const trab = nz(pick(r, ["sbDeTrabalho", "sb_trabalho"], ""), "");
        const enc = nz(pick(r, ["sbEncerramento", "local_encerramento"], ""), "");
        const kmi = nz(
          pick(r, ["kmInicio", "km_inicio", "km_inicial", "kmInicio1"], ""),
          ""
        );
        const kmf = nz(
          pick(r, ["kmFinal", "km_final", "kmFim", "kmFim1"], ""),
          ""
        );
        const h = toHHMM(
          pick(r, ["horario_do_intervalo", "horario_prog", "horario"], "")
        );
        const t = toHHMM(
          pick(r, ["tempo_do_intervalo", "tempo_prog", "tempo_intervalo"], "")
        );
        const l1 = nz(pick(r, ["locomotiva1", "loco1"], ""), "");
        const l2 = nz(pick(r, ["locomotiva2", "loco2"], ""), "");
        const obs = nz(
          pick(r, ["observacao", "obs", "detalhamento", "detalhamento_real"], ""),
          ""
        );

        lines.push(
          `• ${prio} | ${tipo} | Coord: ${coord} | Ativo: ${ativo} | ${ativ} | Qtd: ${qtd} | ` +
            `Partida: ${part} | SB: ${trab} | Enc: ${enc} | KM: ${kmi}-${kmf} | Int: ${h} (${t}) | Ls: ${l1}/${l2}` +
            (obs ? ` | Obs: ${obs}` : "")
        );
      }

      lines.push("");
    }

    return lines.join("\n");
  };

  const onCopyResumo = async () => {
    setErro("");
    setOkMsg("");
    const txt = buildEmailResumo();
    const ok = await copyText(txt);
    if (ok) setOkMsg("✅ Resumo copiado! Cole no e-mail/Teams/WhatsApp.");
    else setErro("Não consegui copiar automaticamente. Tente novamente.");
  };

  return (
    <div
      className="d1page"
      style={{ "--d1-print-scale": String(printScale) }}
      /* ✅ marcas da página para automação */
      data-email-page="programacao-dmais1"
      data-email-page-version="1"
      data-email-date={String(dataFiltro || "")}
    >
      <div className="d1-shell" ref={shellRef}>
        <header
          className="d1-topbar"
          data-email-capture="cards"
          data-email-name="Programação D+1 - Cabeçalho"
        >
          <div className="d1-topbarInner">
            <div
              className="d1-titleBlock"
              data-email-capture="cards"
              data-email-name="Programação D+1 - Título"
            >
              <h1 className="d1-title">PROGRAMAÇÃO D+1</h1>
              <p className="d1-subtitle">{toBRDate(dataFiltro)}</p>
            </div>

            <div className="d1-controls">
              {onBack && (
                <button className="d1-link" onClick={onBack}>
                  ← Menu Principal
                </button>
              )}

              <span className="d1-chip">Gerências: {totalGerencias}</span>
              <span className="d1-chip">Programações: {totalLinhas}</span>

              <label className="d1-label">Filtrar por Data:</label>
              <DateDropdown
                value={dataFiltro}
                onChange={onChangeData}
                ariaLabel="Filtrar por data"
              />

              <button
                className="d1-btn d1-btnGhost"
                type="button"
                onClick={onCopyResumo}
                disabled={carregando || totalLinhas === 0}
                title="Copia um texto pronto para colar no e-mail"
              >
                📋 Copiar resumo
              </button>

              <button
                className="d1-btn d1-btnGhost"
                type="button"
                onClick={onPrint}
                disabled={carregando}
                title="Abrir modo impressão / salvar em PDF"
              >
                🖨️ Imprimir / PDF
              </button>

              {carregando ? <span className="section__muted">Carregando…</span> : null}
            </div>
          </div>
        </header>

        <section
          className="d1-summary"
          data-email-capture="cards"
          data-email-name="Programação D+1 - Resumo"
        >
          <div className="d1-summaryInner">
            <div
              className="d1-summaryLeft"
              data-email-capture="cards"
              data-email-name="Programação D+1 - Resumo (texto)"
            >
              <div className="d1-summaryTitle">
                Programação consolidada para {toBRDate(dataFiltro)}
              </div>
              <div className="d1-summaryNote">
                Gerado em {geradoEm} • Ideal para print/PDF e envio por e-mail
              </div>
            </div>

            <div
              className="d1-summaryRight"
              data-email-capture="cards"
              data-email-name="Programação D+1 - Resumo (KPIs)"
            >
              <span className="d1-kpi">Total Gerências: {totalGerencias}</span>
              <span className="d1-kpi">Total Programações: {totalLinhas}</span>
            </div>
          </div>
        </section>

        {erro ? <div className="d1-error">⚠️ {erro}</div> : null}
        {okMsg ? <div className="d1-ok">{okMsg}</div> : null}

        {!carregando && grupos.length === 0 && !erro ? (
          <div className="d1-empty">Nenhuma programação para a data selecionada.</div>
        ) : null}

        {grupos.map((g) => {
          const gerKey = slugify(g.gerencia);
          const blockId = `programacao-dmais1:gerencia:${gerKey}`;

          return (
            <section
              key={g.gerencia}
              className="d1-card"
              /* ✅ marcas do bloco exportável */
              data-email-block="true"
              data-email-block-id={blockId}
              data-email-block-type="gerencia-table"
              data-email-block-title={g.gerencia}
              data-email-block-count={String(g.itens.length)}
              data-email-capture="table"
              data-email-capture-title={g.gerencia}
            >
              <div className="d1-cardHead">
                <h2 className="d1-gerencia">{g.gerencia}</h2>
                <span className="d1-gerBadge">{g.itens.length} Programações</span>
              </div>

              <FitTable minZoom={0.55}>
                <table className="d1-table">
                  <colgroup>
                    {Array.from({ length: COLS }).map((_, i) => (
                      <col key={i} className="col" />
                    ))}
                  </colgroup>

                  <thead>
                    <tr>
                      <Th>Tipo Programação</Th>
                      <Th>Prioridade</Th>
                      <Th>Coordenador</Th>
                      <Th>Ativo</Th>
                      <Th>Atividade</Th>
                      <Th className="num">Quantidade</Th>
                      <Th>
                        <span className="th-label th--mw th-br">
                          Local de
                          <br />
                          Partida
                        </span>
                      </Th>
                      <Th>SB De Trabalho</Th>
                      <Th>
                        <span className="th-label th--mw th-br">
                          Local de
                          <br />
                          Encerramento
                        </span>
                      </Th>
                      <Th className="num">KM Início</Th>
                      <Th className="num">KM Fim</Th>
                      <Th>Horário Do Intervalo</Th>
                      <Th>Tempo Do Intervalo</Th>
                      <Th>Locomotiva 1</Th>
                      <Th>Locomotiva 2</Th>
                      <Th className="wrap">Observação</Th>
                    </tr>
                  </thead>

                  <tbody>
                    {g.itens.map((r, idx) => {
                      const codigo = pick(r, ["codigo_unico", "key", "id"], "cod");
                      const data = pick(r, ["data", "day"], dataFiltro);

                      return (
                        <tr key={`${codigo}-${data}-${idx}`}>
                          <td className="nowrap">
                            {nz(pick(r, ["tipo_programacao", "tipoProgramacao"], ""))}
                          </td>
                          <td className="nowrap">
                            {nz(pick(r, ["prioridade", "po", "prio"], ""))}
                          </td>
                          <td className="nowrap">
                            {nz(pick(r, ["coordenador"], ""))}
                          </td>
                          <td className="nowrap">{nz(pick(r, ["ativo"], ""))}</td>
                          <td className="wrap">
                            {nz(
                              pick(
                                r,
                                ["atividade", "atividade_prog", "atividade_exibicao"],
                                ""
                              )
                            )}
                          </td>
                          <td className="num nowrap">
                            {nz(
                              pick(
                                r,
                                ["qtd", "quantidade", "quantidade_prog", "quantidade_exibicao"],
                                ""
                              )
                            )}
                          </td>
                          <td className="nowrap">
                            {nz(pick(r, ["sbOrigem", "local_partida", "sb_origem"], ""))}
                          </td>
                          <td className="nowrap">
                            {nz(pick(r, ["sbDeTrabalho", "sb_trabalho"], ""))}
                          </td>
                          <td className="nowrap">
                            {nz(
                              pick(r, ["sbEncerramento", "local_encerramento"], "")
                            )}
                          </td>
                          <td className="num nowrap">
                            {nz(
                              pick(r, ["kmInicio", "km_inicio", "km_inicial", "kmInicio1"], "-"),
                              "-"
                            )}
                          </td>
                          <td className="num nowrap">
                            {nz(
                              pick(r, ["kmFinal", "km_final", "kmFim", "kmFim1"], "-"),
                              "-"
                            )}
                          </td>
                          <td className="nowrap">
                            {toHHMM(
                              pick(r, ["horario_do_intervalo", "horario_prog", "horario"], "")
                            )}
                          </td>
                          <td className="nowrap">
                            {toHHMM(
                              pick(r, ["tempo_do_intervalo", "tempo_prog", "tempo_intervalo"], "")
                            )}
                          </td>
                          <td className="nowrap">
                            {nz(pick(r, ["locomotiva1", "loco1"], "-"), "-")}
                          </td>
                          <td className="nowrap">
                            {nz(pick(r, ["locomotiva2", "loco2"], "-"), "-")}
                          </td>
                          <td className="wrap">
                            {nz(
                              pick(
                                r,
                                ["observacao", "obs", "detalhamento", "detalhamento_real"],
                                ""
                              ),
                              ""
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </FitTable>
            </section>
          );
        })}
      </div>
    </div>
  );
}
